import React, { useEffect, useRef, useState } from "react";
import { ExternalLink } from "lucide-react";
import { api } from "../lib/api";

/**
 * Inline CTA blocks rendered inside a lesson at a given position.
 *
 * Props:
 *   - courseSlug
 *   - lessonId
 *   - courseId          — for click/impression tracking
 *   - position          — "above_video" | "below_video" | "inside_resources"
 */
export default function LessonCTAs({ courseSlug, lessonId, courseId, position = "below_video" }) {
  const [ctas, setCtas] = useState([]);
  const impressionsSent = useRef(new Set());

  useEffect(() => {
    if (!lessonId) return;
    let cancelled = false;
    api.get(`/learn/${courseSlug}/${lessonId}/ctas`).then((r) => {
      if (cancelled) return;
      setCtas((r.data.ctas || []).filter((c) => c.position === position));
    }).catch((e) => console.error("Failed to load CTAs", e));
    return () => { cancelled = true; };
  }, [courseSlug, lessonId, position]);

  // Fire impression once per session per cta
  useEffect(() => {
    ctas.forEach((c) => {
      if (impressionsSent.current.has(c.id)) return;
      impressionsSent.current.add(c.id);
      api.post("/cta-events/impression", {
        kind: "cta_block",
        id: c.id,
        partner_id: c.partner_id,
        course_id: courseId,
        lesson_id: lessonId,
      }).catch((e) => console.error("CTA impression failed", e));
    });
  }, [ctas, courseId, lessonId]);

  const handleClick = (c) => {
    api.post("/cta-events/click", {
      kind: "cta_block",
      id: c.id,
      partner_id: c.partner_id,
      course_id: courseId,
      lesson_id: lessonId,
    }).catch((e) => console.error("CTA click failed", e));
  };

  if (!ctas.length) return null;

  return (
    <div className="space-y-2" data-testid={`lesson-ctas-${position}`}>
      {ctas.map((c) => (
        <div
          key={c.id}
          className="bg-fc-red/10 border border-fc-red/40 flex items-center gap-4 px-5 py-4"
          data-testid={`cta-block-${c.id}`}
        >
          {c.partner.logo_url && (
            <div className="w-10 h-10 bg-white p-1 flex-shrink-0">
              <img
                src={c.partner.logo_url} alt={c.partner.name}
                className="w-full h-full object-contain"
                onError={(e) => { e.target.style.display = "none"; }}
              />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-[10px] tracking-[0.18em] uppercase font-bold text-fc-red/90 mb-0.5">SPONSORED TOOL · {c.partner.category}</p>
            <p className="font-bold text-sm text-white">{c.effective_label}</p>
          </div>
          <a
            href={c.effective_url}
            target="_blank" rel="noopener noreferrer sponsored"
            onClick={() => handleClick(c)}
            className="btn-fc-red text-xs whitespace-nowrap"
            data-testid={`cta-block-cta-${c.id}`}
          >
            Try it now <ExternalLink size={12} />
          </a>
        </div>
      ))}
    </div>
  );
}
